<template>
  <b-card>
    <div class="m-2">
      <!-- Table Top -->
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <label>Show</label>
          <v-select
            v-model="perPage"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="perPageOptions"
            :clearable="false"
            class="per-page-selector d-inline-block ml-50 mr-1"
          />
          <b-button @click="exportToExcel(
              news,
              ['CFU', 'DIVISION', 'UNIT', 'QUARTAL', 'YEAR', 'SALES PLAN', 'MARKET REQUIREMENT', 'PROPOSED PRODUCT', 'NOTES', 'DATE'],
              ['cfu', 'division', 'unit', 'quartal', 'year', 'sales', 'market', 'proposed', 'notes', 'date'],
              ['List Request Product'],
              formatJson
            )" variant="primary"
            >Export Excel</b-button
          >
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-end mb-1 mb-md-0"
        >
          <label class="mr-1">
            Search
          </label>
          <div class="d-flex align-items-center justify-content-end">
            <b-form-input
              class="d-inline-block mr-1"
              placeholder="Search..."
              v-model="searchWord"
            />
          </div>
        </b-col>
      </b-row>
    </div>
    <!-- Larger screen than mobile -->
    <div class="d-none d-md-flex">
      <!-- Table -->
      <b-table responsive :items="news" :fields="fields" :busy="isBusy">
        <template #table-busy>
          <div class="text-center text-danger my-2">
            <b-spinner class="align-middle mr-1" variant="secondary"></b-spinner>
            <strong class="text-secondary">Loading...</strong>
          </div>
        </template>
        <template #cell(solution)="news">
          <div class="d-flex justify-content-start">
              {{ news.item.solution | truncate(100) }}
          </div>
        </template>
        <template #cell(sales_year)="news">
          <div class="d-flex justify-content-start">
              {{ news.item.sales_year | truncate(100) }}
          </div>
        </template>
        <template #cell(attachment)="news">
          <div v-if="news.item.attachment" class="d-flex justify-content-start">
            <b-link target="_blank" :href="news.item.file_url">
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                class="txt-xs-2"
                size="md"
                variant="primary"
              >
                <span
                  ><feather-icon size="14" icon="DownloadIcon" />
                </span>
              </b-button>
            </b-link>
          </div>
          <p v-else>-</p>
        </template>
        <template #cell(action)="news">
          <div class="d-flex justify-content-start">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="secondary"
              class="btn-icon mr-1"
              :to="{ name: 'admin-product-request-detail', params: { key: news.item.id } }"
            >
              <feather-icon icon="EyeIcon" />
            </b-button>
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="danger"
              class="btn-icon"
              @click="confirmDeleteNews(news.item.id)"
            >
              <feather-icon icon="Trash2Icon" />
            </b-button>
          </div>
        </template>
        <template #cell(description)="news">
          <div :inner-html.prop="news.item.content | truncate(250)"></div>
        </template>
        <template #cell(published_at)="news">
          <div>
            {{
              news.item.published_at
                ? $moment(news.item.published_at).format('dddd, Do MMMM YYYY')
                : '-'
            }}
          </div>
        </template>
      </b-table>
    </div>
    <!-- Mobile screen -->
    <div class="d-flex d-md-none flex-column">
      <!-- Loading -->
      <div v-if="isBusy" class="text-center text-danger my-2">
        <b-spinner class="align-middle mr-1" variant="secondary"></b-spinner>
        <strong class="text-secondary">Loading...</strong>
      </div>
      <!-- Data -->
      <b-row
        v-if="!isBusy"
        v-for="(item, index) in news"
        :key="item.id"
        class="mb-3 list-item"
      >
        <b-col cols="12" class="d-flex flex-wrap justify-content-end align-items-center mb-1" style="gap: 1rem;">
          <strong class="flex-grow-1 truncate">{{ item.solution | truncate(100) }}</strong>
          <div>
            <b-link
              v-if="item.attachment"
              target="_blank"
              :href="item.file_url"
              class="mr-1"
            >
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                class="txt-xs-2"
                size="md"
                variant="primary"
              >
                <span>
                  <feather-icon size="14" icon="DownloadIcon" />
                </span>
              </b-button>
            </b-link>
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="secondary"
              class="btn-icon mr-1"
              :to="{ name: 'admin-product-request-detail', params: { key: item.id } }"
            >
              <feather-icon icon="EyeIcon" />
            </b-button>
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="danger"
              class="btn-icon"
              @click="confirmDeleteNews(item.id)"
            >
              <feather-icon icon="Trash2Icon" />
            </b-button>
          </div>
        </b-col>
        <b-col cols="12" class="d-flex flex-wrap">
          <div style="font-size: 0.75rem;">
            <p><strong>CFU:</strong> {{ item.cfu.name }}</p>
            <p><strong>DIVISION:</strong> {{ item.division.division }}</p>
            <p><strong>UNIT:</strong> {{ item.unit }}</p>
            <p><strong>Q:</strong> {{ item.quartal }}</p>
            <p><strong>YEAR:</strong> {{ item.year }}</p>
            <p><strong>SALES PLAN:</strong> {{ item.sales_year | truncate(100) }}</p>
            <p><strong>DATE:</strong> {{ $moment(item.created_at).format('YYYY-MM-DD H:mm') }}</p>
          </div>
        </b-col>
      </b-row>
    </div>
    <div class="mx-2 mb-2">
      <b-row>
        <b-col
          cols="12"
          sm="6"
          class="d-flex align-items-center justify-content-center justify-content-sm-start"
        >
          <span class="text-muted">
            Showing {{ pagination.from }} to {{ pagination.to }} of
            {{ pagination.total }} entries
          </span>
        </b-col>
        <!-- Pagination -->
        <b-col
          cols="12"
          sm="6"
          class="d-flex align-items-center justify-content-center justify-content-sm-end"
        >
          <b-pagination
            v-model="currentPage"
            :total-rows="pagination.total"
            :per-page="perPage"
            first-number
            last-number
            class="mb-0 mt-1 mt-sm-0"
            prev-class="prev-item"
            next-class="next-item"
            @input="changePage"
          >
            <template #prev-text>
              <feather-icon icon="ChevronLeftIcon" size="18" />
            </template>
            <template #next-text>
              <feather-icon icon="ChevronRightIcon" size="18" />
            </template>
          </b-pagination>
        </b-col>
      </b-row>
    </div>
  </b-card>
</template>

<script>
import {
  BCard,
  BCardText,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BMedia,
  BAvatar,
  BLink,
  BBadge,
  BDropdown,
  BDropdownItem,
  BPagination,
  BTooltip,
  BSpinner,
  BFormCheckbox,
  BModal,
  BImg,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'

export default {
  components: {
    BCard,
    BCardText,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BTooltip,
    BSpinner,
    BFormCheckbox,
    BModal,
    BImg,

    vSelect,
  },
  data() {
    return {
      isBusy: false,
      currentPage: 1,
      perPage: 10,
      perPageOptions: [10, 20, 30],
      pagination: {
        from: 1,
        to: 10,
        total: 0,
      },
      fields: [
        { key: 'solution', label: 'Proposed Product' },
        {
          key: 'cfu',
          formatter: (value, key, item) => {
            return value.name
          },
          label: 'CFU'
        },
        {
          key: 'division',
          formatter: (value, key, item) => {
            return value.division
          },
          label: 'Division'
        },
        { key: 'unit', label: 'Unit' },
        { key: 'quartal', label: 'Q' },
        { key: 'year', label: 'Year' },
        { key: 'sales_year', label: 'Sales Plan' },
        { key: 'attachment', label: 'File' },
        'action',
        {
          key: 'created_at',
          formatter: (value, key, item) => {
            return this.$moment(value).format('YYYY-MM-DD H:mm')
          },
          label: 'Date'
        },
      ],
      news: [],
      searchWord: '',
    }
  },

  directives: {
    Ripple,
  },

  filters: {
    truncate: function(value, length, start = 0) {
      if (!value) return ''
      value = value.toString().replace(/<[^>]*>?/gm, '');
      if (value.length > length) {
        if (start > 0) {
          return value.substring(start, length + start) + '...'
        } else {
          return value.substring(start, length) + '...'
        }
      } else {
        return value
      }
    },
  },

  watch: {
    perPage() {
      this.fetchNews()
    },
    currentPage() {
      this.fetchNews()
    },
    searchWord() {
      this.fetchNews()
    },
  },

  mounted() {
    this.fetchNews()
  },

  methods: {
    fetchNews() {
      this.isBusy = true

      this.$http
        .get('/request_prod', {
          params: {
            page: this.currentPage,
            perPage: this.perPage,
            s: this.searchWord.toString().trim(),
          },
        })
        .then(({ data }) => {
          this.currentPage = data.current_page
          this.pagination.total = data.total
          this.pagination.from = data.from
          this.pagination.to = data.to
          this.news = data.data
        })
        .finally(() => {
          this.isBusy = false
        })
    },
    changePage(page) {
      this.currentPage = page
    },
    async deleteNews(newsId) {
      this.isBusy = true
      return await this.$http
        .delete(`/request_prod/delete/${newsId}`)
        .then(res => {
          this.$notif(
            'Success',
            'Product Request has been successfully deleted.',
            'success'
          )
        })
        .catch(({ data }) => {
          this.$notif('Something Error', data.message, 'danger')
        })
        .finally(() => {
          this.isBusy = false
        })
    },
    confirmDeleteNews(newsId) {
      this.$bvModal
        .msgBoxConfirm('Please confirm that you want to delete this item.', {
          title: 'Please Confirm',
          size: 'sm',
          okVariant: 'danger',
          okTitle: 'Yes',
          cancelTitle: 'No',
          cancelVariant: 'outline-secondary',
          hideHeaderClose: false,
          centered: true,
        })
        .then(value => {
          if (value === true) {
            this.deleteNews(newsId).then(() => {
              this.fetchNews()
            })
          }
        })
    },
    exportToExcel (records, headers, fieldVal, filename, formatJson) {
      import('@/libs/Export2Excel').then(excel => {
          const list = records
          const data = formatJson(fieldVal, list)
          excel.export_json_to_excel({
            header: headers,
            data,
            filename,
            autoWidth: true,
            bookType: 'xlsx'
          })
      })
    },
    formatJson (filterVal, jsonData) {
      return jsonData.map(v => filterVal.map(j => {
        let data = null
        data = v[j]

        if (j === 'date') {
          data = this.$moment(v.created_at).format('D-M-Y')
        }

        if (j === 'cfu') {
          data = v.cfu.name
        }

        if (j === 'division') {
          data = v.division.division
        }

        if (j === 'unit') {
          data = v.unit
        }

        if (j === 'quartal') {
          data = v.quartal
        }

        if (j === 'year') {
          data = v.year
        }

        if (j === 'sales') {
          data = v.sales_year
        }

        if (j === 'market') {
          data = v.ci.replace(/(<([^>]+)>)/gi, "")
        }

        if (j === 'proposed') {
          data = v.solution.replace(/(<([^>]+)>)/gi, "")
        }

        if (j === 'notes') {
          data = v.notes.replace(/(<([^>]+)>)/gi, "")
        }

        return data
      }))
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
